import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from 'zignal-landscape/lib/Button';
import Link from 'zignal-landscape/lib/Link';
import Modal from 'zignal-landscape/lib/Modal';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';

import { DashboardSettingsErrors, DashboardSettingsForm } from './components';
import {
  clear as clearAction,
  clearError as clearErrorAction,
  clearErrors as clearErrorsAction,
  setError as setErrorAction,
  setFocus as setFocusAction,
  focused as focusedAction,
  setValue as setValueAction,
  saveStarted as saveStartedAction,
  saveCompleted as saveCompletedAction
} from './DashboardSettingsModal.ducks';

import {
  mapProfilesToOptions,
  getSelectedProfiles,
  getDefaultProfile,
  getDefaultDateRange
} from './helpers';

import Styles from './components/DashboardSettingsModal.css';

const DEFAULT_DASHBOARDS = [];
const DEFAULT_TEMPLATE_TYPES = [];
const DEFAULT_DATE_RANGES = [];
const DEFAULT_PROFILES = [];

const DashboardSettingsModal = ({
  dashboards = DEFAULT_DASHBOARDS,
  templateTypes = DEFAULT_TEMPLATE_TYPES,
  dateRanges = DEFAULT_DATE_RANGES,
  hasTemplateChoices,
  initialValues,
  isNew,
  onSave,
  profiles = DEFAULT_PROFILES,
  typeLabel = '',
  typeLabelPlural = '',
  /* Redux props */
  clear,
  clearError,
  clearErrors,
  setError,
  errors,
  focused,
  isSaving,
  isSubmitted,
  shouldFocus,
  saveStarted,
  setFocus,
  setValue,
  values
}) => {
  const containerRef = useRef();
  const [isOpen, setIsOpen] = useState(true);
  const timezone = window?.Account?.getTimezone?.() || 'UTC';

  // Profile options
  const profilesOptions = mapProfilesToOptions(profiles);

  // Initial values for edition
  useEffect(() => {
    const {
      title = '',
      selected_profiles: selectedProfiles,
      defaultProfileId,
      defaultDateRange,
      customDateRangeStart,
      customDateRangeEnd
    } = initialValues;

    const initialProfiles = getSelectedProfiles(
      selectedProfiles,
      profilesOptions
    );

    const initialDefaultProfile = getDefaultProfile(
      defaultProfileId,
      profilesOptions
    );

    const initialDefaultDateRange = getDefaultDateRange(
      defaultDateRange,
      dateRanges
    );

    setValue('title', title);
    setValue('profiles', initialProfiles);
    setValue('defaultProfile', initialDefaultProfile);
    setValue('defaultDateRange', initialDefaultDateRange);
    setValue('customDateRangeStart', customDateRangeStart || null);
    setValue('customDateRangeEnd', customDateRangeEnd || null);
    setValue('allProfilesSelected', initialProfiles === null);
  }, [initialValues]);

  // Open/close the modal
  function toggleOpen() {
    setIsOpen(!isOpen);
    isOpen && clear();
  }

  // Submit the form
  function handleSubmit() {
    clearErrors();
    if (!values.title.trim()) {
      setError('title', `${typeLabelPlural} must have a title.`);
      setFocus('title');
      return containerRef.current?.scrollTo?.(0, 0);
    } else if (!values.allProfilesSelected && !values.profiles?.length) {
      setError('profiles', 'Please select at least 1 dataset.');
      return containerRef.current?.scrollTo?.(0, 0);
    } else if (values.defaultDateRange?.value === 'custom' &&
               (!values.customDateRangeStart || !values.customDateRangeEnd)) {
      setError('title', 'Please select start and end dates for the custom date range.');
      return containerRef.current?.scrollTo?.(0, 0);
    }

    saveStarted();
    return onSave(values, () => {
      toggleOpen();
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onBackdropClick={toggleOpen}
      onEscapePress={toggleOpen}
      size="medium"
    >
      <ModalHeader onClose={toggleOpen}>
        {`${typeLabel} Settings`.toUpperCase()}
      </ModalHeader>

      <div className={Styles.modalBodyWrapper} ref={containerRef}>
        <ModalBody>
          <DashboardSettingsErrors errors={errors} onClearError={clearError} />
          <DashboardSettingsForm
            dashboards={dashboards}
            dateRanges={dateRanges}
            focused={focused}
            hasTemplateChoices={hasTemplateChoices}
            isNew={isNew}
            isSubmitted={isSubmitted}
            onSubmit={handleSubmit}
            profileOptions={profilesOptions}
            setValue={setValue}
            shouldFocus={shouldFocus}
            templateTypes={templateTypes}
            timezone={timezone}
            typeLabel={typeLabel}
            typeLabelPlural={typeLabelPlural}
            values={values}
            scrollContainerRef={containerRef}
          />
        </ModalBody>
      </div>
      <ModalFooter>
        <div className={Styles.buttonsWrapper}>
          <span className={Styles.cancelWrapper}>
            <Link linkType="cancel" onClick={toggleOpen} disabled={isSaving} data-cy="dashboard-settings-cancel">
              Cancel
            </Link>
          </span>
          <Button
            buttonStyle="positive"
            onClick={handleSubmit}
            disabled={isSaving}
            data-cy="dashboard-settings-save"
          >
            {isNew ? 'Create' : 'Save'} {typeLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

DashboardSettingsModal.propTypes = {
  dashboards: PropTypes.array,
  dateRanges: PropTypes.array,
  hasTemplateChoices: PropTypes.bool,
  initialValues: PropTypes.any,
  isNew: PropTypes.bool,
  onSave: PropTypes.func,
  profiles: PropTypes.array,
  templateTypes: PropTypes.array,
  typeLabel: PropTypes.string,
  typeLabelPlural: PropTypes.string,

  /* Redux Props */
  clearError: PropTypes.func,
  clearErrors: PropTypes.func,
  clear: PropTypes.func,
  setError: PropTypes.func,
  setFocus: PropTypes.func,
  saveStarted: PropTypes.func,
  saveCompleted: PropTypes.func,
  errors: PropTypes.any,
  focused: PropTypes.func,
  isSaving: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  shouldFocus: PropTypes.any,
  setValue: PropTypes.func,
  values: PropTypes.any
};

export const mapStateToProps = (state) => {
  return {
    errors: state.DashboardSettingsModal.errors,
    isSaving: state.DashboardSettingsModal.isSaving,
    isSubmitted: state.DashboardSettingsModal.isSubmitted,
    shouldFocus: state.DashboardSettingsModal.shouldFocus,
    values: state.DashboardSettingsModal.values
  };
};

const mapDispatchToProps = {
  clear: clearAction,
  clearError: clearErrorAction,
  clearErrors: clearErrorsAction,
  focused: focusedAction,
  setError: setErrorAction,
  setFocus: setFocusAction,
  setValue: setValueAction,
  saveStarted: saveStartedAction,
  saveCompleted: saveCompletedAction
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardSettingsModal);
